import { toIsoTimestamp } from "./detect";
import { stableUuid } from "./id";
import { BACKUP_FORMAT, BACKUP_FORMAT_VERSION } from "./schema";
import type { ClinicalOSBackupFile, SubstanceRef } from "./types";

/** Estruturas do backup Piru (piruExportVersion = 1). */
export interface PiruDose {
  id?: string;
  amount?: number;
  unit?: string;
  route?: string;
  substance?: string;
  tags?: string[];
  timestamp?: number | string;
  isBackgroundMed?: boolean;
  displayNameSnapshot?: string;
  substanceUID?: string;
  isomer?: string;
  releaseForm?: string;
  productName?: string;
  saltForm?: string;
  isApproximate?: boolean;
  isUnknownDose?: boolean;
  hadGrapefruit?: boolean;
  meal?: string;
  volumeML?: number;
  abv?: number;
  drinkName?: string;
  combinationName?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
  locationName?: string;
}
export interface PiruNote {
  id?: string;
  kind?: string;
  text?: string;
  timestamp?: number | string;
  descriptors?: string[];
  energy?: number;
  mood?: number;
  social?: number;
  worked?: number;
  heartRate?: number;
  shulgin?: string | number;
}
export interface PiruSession {
  id?: string;
  title?: string;
  startDate?: number | string;
  note?: string;
  doses?: PiruDose[];
  notes?: PiruNote[];
  checkInIntervalMinutes?: number;
  checkInOffsetMinutes?: number[];
  checkInOffered?: boolean;
  checkInFollowsDoses?: boolean;
}
export interface PiruInventoryEvent {
  id?: string;
  amount?: number;
  date?: number | string;
  kind?: string;
  note?: string;
  setsBaseline?: boolean;
}
export interface PiruInventoryItem {
  id?: string;
  sortOrder?: number;
  substance?: string;
  saltForm?: string;
  unit?: string;
  doseSize?: number;
  unitStrengthMG?: number;
  baselineQuantity?: number;
  lowStockThreshold?: number;
  createdAt?: number | string;
  trackingStart?: number | string;
  manualEvents?: PiruInventoryEvent[];
}
export interface PiruBackup {
  piruExportVersion?: number;
  appVersion?: string;
  exportedAt?: number | string;
  sessions?: PiruSession[];
  orphanDoses?: PiruDose[];
  inventory?: PiruInventoryItem[];
  favorites?: { substance?: string; createdAt?: number | string }[];
  substanceColors?: { substance?: string; hexColor?: string }[];
  userColors?: unknown[];
  customSubstances?: Record<string, any>[];
  dailyDoseItems?: unknown[];
}

export interface PiruPreview {
  appVersion: string | null;
  exportedAt: string | null;
  sessions: number;
  doses: number;
  orphanDoses: number;
  notes: number;
  inventoryItems: number;
  inventoryMovements: number;
  favorites: number;
  substanceColors: number;
  customSubstances: number;
  dailyDoseItems: number;
  substances: string[];
  periodStart: string | null;
  periodEnd: string | null;
  warnings: string[];
}


function finiteNonNegative(value: unknown): number | null {
  const n = typeof value === "number" ? value : value == null || value === "" ? NaN : Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function validAbv(value: unknown): number | null {
  const n = finiteNonNegative(value);
  return n != null && n <= 100 ? n : null;
}

function ethanolGrams(volumeMl: unknown, abv: unknown): number | null {
  const volume = finiteNonNegative(volumeMl);
  const percentage = validAbv(abv);
  if (volume == null || percentage == null) return null;
  return Math.round(volume * (percentage / 100) * 0.789 * 1000) / 1000;
}

function normalized(value: unknown): string {
  return String(value ?? "").trim().toLocaleLowerCase("pt-BR");
}

function sourceKey(prefix: string, explicitId: unknown, fallback: string): string {
  const raw = String(explicitId ?? "").trim();
  return raw ? `${prefix}:id:${raw}` : `${prefix}:fallback:${fallback}`;
}

function movementType(kind: unknown): "entrada" | "saida" | "ajuste" {
  const value = normalized(kind);
  if (["restock", "refill", "purchase", "entrada", "add"].includes(value)) return "entrada";
  if (["use", "used", "consume", "consumed", "saida", "remove"].includes(value)) return "saida";
  return "ajuste";
}

/** Leitura somente-análise do backup Piru: contagens, período e avisos. */
export function previewPiru(raw: PiruBackup): PiruPreview {
  const sessions = raw.sessions ?? [];
  const orphan = raw.orphanDoses ?? [];
  const allDoses = [...sessions.flatMap((s) => s.doses ?? []), ...orphan];
  const notes = sessions.flatMap((s) => s.notes ?? []);
  const inventory = raw.inventory ?? [];
  const times = [
    ...allDoses.map((d) => toIsoTimestamp(d.timestamp)),
    ...sessions.map((s) => toIsoTimestamp(s.startDate)),
    ...notes.map((no) => toIsoTimestamp(no.timestamp)),
  ].filter((v): v is string => !!v).sort();
  const substances = Array.from(
    new Set(allDoses.map((d) => (d.substance ?? d.displayNameSnapshot ?? "").trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const warnings: string[] = [
    "Este formato não contém identidade clínica: escolha o paciente de destino.",
    "Nada de tratamento ativo é criado automaticamente a partir de doses importadas.",
  ];
  if (allDoses.some((d) => !toIsoTimestamp(d.timestamp)))
    warnings.push("Algumas doses não têm data válida e serão ignoradas.");
  if (allDoses.some((d) => d.latitude || d.longitude || d.locationName))
    warnings.push("Há localização nas doses: será preservada apenas em metadados do evento importado.");
  if ((raw.customSubstances ?? []).length)
    warnings.push(`${(raw.customSubstances ?? []).length} substância(s) personalizada(s) serão criadas como privadas.`);

  return {
    appVersion: raw.appVersion ?? null,
    exportedAt: toIsoTimestamp(raw.exportedAt),
    sessions: sessions.length,
    doses: allDoses.length,
    orphanDoses: orphan.length,
    notes: notes.length,
    inventoryItems: inventory.length,
    inventoryMovements: inventory.reduce((acc, i) => acc + (i.manualEvents ?? []).length, 0),
    favorites: (raw.favorites ?? []).length,
    substanceColors: (raw.substanceColors ?? []).length,
    customSubstances: (raw.customSubstances ?? []).length,
    dailyDoseItems: (raw.dailyDoseItems ?? []).length,
    substances,
    periodStart: times[0] ?? null,
    periodEnd: times[times.length - 1] ?? null,
    warnings,
  };
}

/**
 * Converte um backup Piru em ClinicalOS Backup v1.
 * O mapeamento é fiel: não faz interpretação clínica nem cria tratamento ativo.
 * IDs são determinísticos para que importar o mesmo arquivo novamente seja idempotente.
 */
export function piruToClinicalOSBackup(raw: PiruBackup, patientId: string, ownerId: string): ClinicalOSBackupFile {
  const sessions: Record<string, any>[] = [];
  const doses: Record<string, any>[] = [];
  const events: Record<string, any>[] = [];
  const inventories: Record<string, any>[] = [];
  const movements: Record<string, any>[] = [];
  const substanceRefs = new Map<string, SubstanceRef>();
  const geo: Record<string, any> = {};

  const refFor = (name: string): string => {
    const clean = name.trim();
    const key = normalized(clean);
    if (!substanceRefs.has(key)) {
      substanceRefs.set(key, { id: stableUuid("piru:substance", key), name: clean, is_global: null });
    }
    return substanceRefs.get(key)!.id;
  };

  const addDose = (dose: PiruDose, sessionId: string | null, indexKey: string) => {
    const at = toIsoTimestamp(dose.timestamp);
    if (!at) return;
    const substanceName = (dose.substance ?? dose.displayNameSnapshot ?? "Substância não identificada").trim();
    const sourceId = sourceKey(
      "dose",
      dose.id,
      `${indexKey}|${at}|${substanceName}|${dose.amount ?? ""}|${dose.unit ?? ""}|${dose.route ?? ""}`,
    );
    const doseId = stableUuid("piru:dose", sourceId);
    if (dose.latitude || dose.longitude || dose.locationName) {
      geo[sourceId] = { latitude: dose.latitude, longitude: dose.longitude, locationName: dose.locationName };
    }
    doses.push({
      id: doseId,
      patient_id: patientId,
      session_id: sessionId,
      substance_id: substanceName ? refFor(substanceName) : null,
      substance_name: substanceName || "Substância não identificada",
      dose_amount: typeof dose.amount === "number" && Number.isFinite(dose.amount) ? dose.amount : null,
      dose_unit: dose.unit ?? null,
      route: dose.route ?? null,
      actual_time: at,
      log_type: dose.isBackgroundMed ? "manutencao" : "pontual",
      patient_notes: dose.notes ?? null,
      formulation: [
        dose.productName,
        dose.releaseForm,
        dose.saltForm ? `sal: ${dose.saltForm}` : null,
      ].filter(Boolean).join(" · ") || null,
      formulation_name: dose.displayNameSnapshot ?? dose.productName ?? null,
      beverage_consumed_ml: finiteNonNegative(dose.volumeML),
      beverage_abv_percent: validAbv(dose.abv),
      beverage_name: dose.drinkName ?? null,
      ethanol_grams: ethanolGrams(dose.volumeML, dose.abv),
      food_context: dose.meal ?? null,
      dose_text: [
        dose.displayNameSnapshot,
        dose.productName,
        (dose.tags ?? []).join(", "),
        dose.isApproximate ? "dose aproximada" : null,
        dose.isUnknownDose ? "dose desconhecida" : null,
        dose.hadGrapefruit ? "grapefruit" : null,
        dose.combinationName ? `combinação: ${dose.combinationName}` : null,
      ].filter(Boolean).join(" · ") || null,
    });
    const doseRow = doses[doses.length - 1];
    events.push({
      id: stableUuid("piru:event:dose", sourceId),
      patient_id: patientId,
      session_id: sessionId,
      linked_dose_id: doseId,
      event_at: at,
      event_type: "dose",
      title: `${doseRow.substance_name}${doseRow.dose_amount !== null ? ` ${doseRow.dose_amount}${doseRow.dose_unit ?? ""}` : ""}`,
      description: dose.notes ?? null,
      data_origin: "importado",
      is_retroactive: true,
      dedupe_key: `piru:${sourceId}`,
      source_table: "medication_dose_logs",
      metadata: {
        source_app: "piru",
        source_id: String(dose.id ?? ""),
        substance_uid: dose.substanceUID ?? null,
        tags: dose.tags ?? [],
        is_background_med: dose.isBackgroundMed ?? false,
        location: geo[sourceId] ?? null,
        salt_form: dose.saltForm ?? null,
        isomer: dose.isomer ?? null,
        release_form: dose.releaseForm ?? null,
        product_name: dose.productName ?? null,
        display_name_snapshot: dose.displayNameSnapshot ?? null,
        is_approximate: dose.isApproximate ?? false,
        is_unknown_dose: dose.isUnknownDose ?? false,
        had_grapefruit: dose.hadGrapefruit ?? false,
        meal: dose.meal ?? null,
        beverage_volume_ml: finiteNonNegative(dose.volumeML),
        beverage_abv_percent: validAbv(dose.abv),
        drink_name: dose.drinkName ?? null,
        combination_name: dose.combinationName ?? null,
      },
    });
  };

  (raw.sessions ?? []).forEach((session, sessionIndex) => {
    const startedAt = toIsoTimestamp(session.startDate) ?? new Date(0).toISOString();
    const sessionSourceKey = sourceKey("session", session.id, `${sessionIndex}|${startedAt}|${session.title ?? ""}`);
    const id = stableUuid("piru:session", sessionSourceKey);
    sessions.push({
      id,
      patient_id: patientId,
      session_at: startedAt,
      name: session.title?.trim() || "Sessão importada (Piru)",
      session_type: "importada",
      status: "fechada",
      session_status: "realizada",
      is_retroactive: true,
      notes: [
        session.note?.trim(),
        `Importado de backup Piru · origem ${session.id ?? `sessão ${sessionIndex + 1}`}`,
        session.checkInIntervalMinutes != null ? `Cadência de check-in Piru: ${session.checkInIntervalMinutes} min` : null,
        session.checkInOffsetMinutes?.length ? `Horários de check-in Piru (min): ${session.checkInOffsetMinutes.join(", ")}` : null,
        session.checkInFollowsDoses != null ? `Check-in segue doses: ${session.checkInFollowsDoses ? "sim" : "não"}` : null,
      ].filter(Boolean).join("\n"),
    });
    (session.doses ?? []).forEach((dose, doseIndex) => addDose(dose, id, `${sessionSourceKey}|${doseIndex}`));
    (session.notes ?? []).forEach((note, noteIndex) => {
      const at = toIsoTimestamp(note.timestamp);
      if (!at) return;
      const noteSourceKey = sourceKey("note", note.id, `${sessionSourceKey}|${noteIndex}|${at}|${note.text ?? ""}`);
      events.push({
        id: stableUuid("piru:event:note", noteSourceKey),
        patient_id: patientId,
        session_id: id,
        linked_session_id: id,
        event_at: at,
        event_type: note.kind === "experience" ? "experiencia" : "observacao",
        title: note.kind === "experience" ? "Relato de experiência (Piru)" : "Anotação (Piru)",
        description: note.text ?? null,
        data_origin: "importado",
        is_retroactive: true,
        dedupe_key: `piru:${noteSourceKey}`,
        metadata: {
          source_app: "piru",
          source_id: note.id ?? null,
          kind: note.kind ?? null,
          descriptors: note.descriptors ?? [],
          energy: note.energy ?? null,
          mood: note.mood ?? null,
          shulgin: note.shulgin ?? null,
          social: note.social ?? null,
          worked: note.worked ?? null,
          heart_rate: note.heartRate ?? null,
        },
      });
    });
  });

  (raw.orphanDoses ?? []).forEach((dose, index) => addDose(dose, null, `orphan|${index}`));

  (raw.inventory ?? []).forEach((item, inventoryIndex) => {
    const itemName = item.substance?.trim() || "Item importado (Piru)";
    const createdAt = toIsoTimestamp(item.createdAt);
    const trackingStart = toIsoTimestamp(item.trackingStart);
    const invSourceKey = `${item.id ?? inventoryIndex}|${normalized(itemName)}|${createdAt ?? ""}|${item.unit ?? ""}`;
    const invId = stableUuid("piru:inventory", invSourceKey);
    inventories.push({
      id: invId,
      patient_id: patientId,
      medication_name: itemName,
      substance_id: item.substance ? refFor(item.substance) : null,
      unit: item.unit?.trim() || "un",
      current_quantity:
        typeof item.baselineQuantity === "number" && Number.isFinite(item.baselineQuantity) ? item.baselineQuantity : 0,
      low_stock_threshold: finiteNonNegative(item.lowStockThreshold) ?? 0,
      package_description: item.doseSize != null ? `Tamanho de dose Piru: ${item.doseSize} ${item.unit ?? ""}` : null,
      ...(createdAt ? { created_at: createdAt } : {}),
      notes: [
        "origem=piru",
        item.saltForm ? `saltForm=${item.saltForm}` : null,
        item.unitStrengthMG != null ? `unitStrengthMG=${item.unitStrengthMG}` : null,
        `trackingStart=${trackingStart ?? "?"}`,
        item.sortOrder != null ? `sortOrder=${item.sortOrder}` : null,
      ].filter(Boolean).join("; "),
    });
    (item.manualEvents ?? []).forEach((ev, movementIndex) => {
      if (typeof ev.amount !== "number" || !Number.isFinite(ev.amount)) return;
      const at = toIsoTimestamp(ev.date);
      const movementSourceKey = sourceKey("inventory-event", ev.id, `${invSourceKey}|${movementIndex}|${at ?? ""}|${ev.amount}`);
      movements.push({
        id: stableUuid("piru:inventory-event", movementSourceKey),
        patient_id: patientId,
        inventory_id: invId,
        movement_type: movementType(ev.kind),
        amount: ev.amount,
        unit: item.unit?.trim() || "un",
        ...(at ? { created_at: at } : {}),
        quantity_before: null,
        quantity_after: null,
        reason: [`origem=piru`, `kind=${ev.kind ?? "?"}`, `setsBaseline=${ev.setsBaseline ? "sim" : "nao"}`, `sourceId=${ev.id ?? "?"}`, ev.note ?? null].filter(Boolean).join("; "),
      });
    });
  });

  const appSettings = [
    {
      owner_id: ownerId,
      preferences: {
        imported_piru: {
          importedAt: new Date().toISOString(),
          sourceExportedAt: toIsoTimestamp(raw.exportedAt),
          appVersion: raw.appVersion ?? null,
          favorites: raw.favorites ?? [],
          substanceColors: raw.substanceColors ?? [],
          userColors: raw.userColors ?? [],
          dailyDoseItems: raw.dailyDoseItems ?? [],
        },
      },
    },
  ];

  const customSubstances = (raw.customSubstances ?? [])
    .map((sub, index) => {
      const name = (sub.name ?? sub.substance ?? "").toString().trim();
      if (!name) return null;
      const duration = sub.duration && typeof sub.duration === "object" ? sub.duration : {};
      const rangeValue = (phase: any, bound: "min" | "max") => {
        const value = phase && typeof phase === "object" ? Number(phase[bound]) : NaN;
        return Number.isFinite(value) && value >= 0 ? value : null;
      };
      const halfLifeMinutes = Number(sub.halfLifeMinutes);
      const sourcePayload = JSON.stringify({
        source_app: "Piru",
        source_category: sub.category ?? null,
        unit: sub.unit ?? null,
        doses: sub.doses ?? null,
        duration: sub.duration ?? null,
        components: sub.components ?? null,
        displayName: sub.displayName ?? null,
      });
      return {
        id: refFor(name),
        name,
        generic_name: sub.displayName ?? null,
        owner_id: ownerId,
        is_global: false,
        is_active: true,
        default_route: sub.defaultRoute ?? "oral",
        default_dose_unit: sub.unit ?? "mg",
        dose_unit_default: sub.unit ?? "mg",
        clinical_category: sub.category ?? "Other",
        pharmacological_class: sub.category ?? "Other",
        short_description: sub.notes ?? null,
        source_notes: [
          `Importado de backup Piru · item personalizado ${index + 1}`,
          "Conteúdo original do item customizado armazenado para rastreabilidade: " + sourcePayload,
        ].join("\n"),
        half_life_min_value: Number.isFinite(halfLifeMinutes) && halfLifeMinutes >= 0 ? halfLifeMinutes : null,
        half_life_max_value: Number.isFinite(halfLifeMinutes) && halfLifeMinutes >= 0 ? halfLifeMinutes : null,
        half_life_unit: "minutos",
        onset_min_value: rangeValue(duration.onset, "min"),
        onset_max_value: rangeValue(duration.onset, "max"),
        onset_unit: "minutos",
        comeup_min_value: rangeValue(duration.comeup, "min"),
        comeup_max_value: rangeValue(duration.comeup, "max"),
        comeup_unit: "minutos",
        peak_min_value: rangeValue(duration.peak, "min"),
        peak_max_value: rangeValue(duration.peak, "max"),
        peak_unit: "minutos",
        offset_min_value: rangeValue(duration.offset, "min"),
        offset_max_value: rangeValue(duration.offset, "max"),
        offset_unit: "minutos",
        residual_min_value: rangeValue(duration.afterglow, "min"),
        residual_max_value: rangeValue(duration.afterglow, "max"),
        residual_unit: "minutos",
        total_duration_min_value: rangeValue(duration.total, "min"),
        total_duration_max_value: rangeValue(duration.total, "max"),
        total_duration_unit: "minutos",
        requires_clinical_review: true,
        review_status: "importado Piru · revisão necessária",
      };
    })
    .filter(Boolean) as Record<string, any>[];

  const tables: Record<string, Record<string, any>[]> = {
    app_settings: appSettings,
    clinical_sessions: sessions,
    medication_dose_logs: doses,
    patient_psylog_events: events,
    medication_inventory: inventories,
    medication_inventory_movements: movements,
    substances: customSubstances,
  };
  const counts: Record<string, number> = {};
  for (const [k, v] of Object.entries(tables)) counts[k] = v.length;

  return {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    app: "ClinicalOS",
    appVersion: raw.appVersion,
    exportedAt: toIsoTimestamp(raw.exportedAt) ?? new Date().toISOString(),
    exportedBy: "piru-import",
    scope: {
      kind: "patient",
      patientId,
      includePharmacology: true,
      includeDerived: false,
      includeAuditHistory: false,
    },
    counts,
    integrity: { algorithm: "SHA-256", payloadHash: null },
    excluded: ["nenhum dado clínico interpretado; restauração fiel do backup Piru"],
    data: {
      tables,
      substanceRefs: Array.from(substanceRefs.values()),
      formulationRefs: [],
      groups: {
        app_settings: "core",
        clinical_sessions: "core",
        medication_dose_logs: "core",
        patient_psylog_events: "core",
        medication_inventory: "core",
        medication_inventory_movements: "core",
        substances: "pharm_private",
      },
    },
  };
}
